import React, { useContext, useRef } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { WebSocketContext } from '../../context/WebSocketContext';
import { RingingBellBadge } from './RingingBellBadge';
import connectActiveReminderCount, { ActiveReminderCountProps } from './connectActiveReminderCount';

// A background tab Chrome has left this long is stale. If its socket is still open, the socket has
// kept the count current (ReminderAlertStack takes it from each message, and re-reads it after a
// reconnect), so nothing is fetched; without one, the count is re-read on return. A tab Chrome
// discarded reloads instead, and reads it on mount.
const STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * A ringing red bell beside "Work Orders" in the side menu while any of the user's reminders are
 * due. The count is read when the app loads and on returning to a stale tab with no live socket;
 * star writes refresh it, and a reminder firing over the socket adds one.
 */
const SiderReminderBell: React.FC<ActiveReminderCountProps> = ({
	activeReminderCount,
	refreshCount,
}) => {
	const socketContext = useContext(WebSocketContext);
	// The latest socket, for the visibility listener registered once on mount.
	const socketRef = useRef<WebSocket | null>(null);
	socketRef.current = socketContext ? socketContext.socket : null;

	useMountEffect(() => {
		refreshCount();
		let hiddenAt: number | null = null;
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAt = Date.now();
			} else if (hiddenAt !== null && Date.now() - hiddenAt >= STALE_AFTER_MS) {
				const socket = socketRef.current;
				if (!socket || socket.readyState !== WebSocket.OPEN) refreshCount();
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	});

	if (activeReminderCount <= 0) return null;
	// Right-aligned with the Inbox count badge below it.
	return (
		<RingingBellBadge
			title={`${activeReminderCount} reminder${activeReminderCount === 1 ? '' : 's'} due`}
			className="ml-2 self-center"
		/>
	);
};

export default connectActiveReminderCount(SiderReminderBell);
